import { CONTENT_ACTIONS, type ContentAction, type ContentActionParams } from '~/composables/useContentActions'

/**
 * Decodes a `postMessage` from a Braze Custom Code banner. The SDK renders banners as a same-origin
 * `srcdoc` iframe, so `event.origin` matches our own origin and can't distinguish a banner from any
 * other same-origin sender: every field is validated here behind the `mhj-banner` tag, a mandatory
 * per-session token, the CONTENT_ACTIONS allow-list and a per-parameter allow-list.
 *
 * Source matching is the caller's job — see `components/cms/BrazeBanner.vue`, which also holds the
 * message contract and authoring snippet.
 */

const SAFE_VALUE = /^[A-Za-z0-9_-]{1,64}$/
const MAX_URL_LENGTH = 512

const safeValue = (value: string | undefined): string | undefined => {
  const text = value?.trim()
  return text && SAFE_VALUE.test(text) ? text : undefined
}

const hasControlChars = (value: string) =>
  [...value].some((char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f)

/**
 * `url` reaches the router from an untrusted iframe, so only same-origin relative paths pass: one
 * leading slash (rejecting protocol-relative `//evil.com`), no backslash (browsers fold `\` to `/`)
 * and no control characters. Refusing every scheme also rules out `javascript:` and `data:`.
 */
export const isSafeInternalPath = (value: string): boolean =>
  value.length > 0 &&
  value.length <= MAX_URL_LENGTH &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.includes('\\') &&
  !hasControlChars(value)

export const isContentAction = (value: string | null | undefined): value is ContentAction =>
  CONTENT_ACTIONS.some((action) => action === value)

/** The banner-authored params, before validation. Values arrive untrusted from the SDK or iframe. */
export type RawActionParams = {
  productId?: unknown
  couponCode?: unknown
  url?: unknown
}

/**
 * Each parameter is validated against its own rule rather than one shared pattern: `url` is a path
 * and would always fail the identifier rule that `productId` and `couponCode` need.
 */
export const sanitizeParams = (raw: RawActionParams): ContentActionParams => {
  const params: ContentActionParams = {}

  const productId = safeValue(asString(raw.productId))
  if (productId) params.productId = productId

  const couponCode = safeValue(asString(raw.couponCode))
  if (couponCode) params.couponCode = couponCode

  const url = asString(raw.url)?.trim()
  if (url && isSafeInternalPath(url)) params.url = url

  return params
}

export type BannerMessage = {
  /**
   * The storefront action to run, or null when the banner only wants the click logged for work it
   * already completed inside its own iframe (e.g. the #260908 newsletter form).
   */
  action: ContentAction | null
  params: ContentActionParams
  /** Reporting label for the click; the caller sanitizes it before use. */
  ctaText: string | undefined
}

/** Returns `null` for anything that isn't a trusted, fully-valid banner message. */
export const decodeBannerMessage = (event: MessageEvent, sessionToken: string | undefined): BannerMessage | null => {
  // The banner iframe is same-origin, so `event.data` carries no trust and is parsed field by field.
  const raw: unknown = event.data
  if (!isRecord(raw) || raw.source !== 'mhj-banner') return null

  // Without the per-session token, any other frame could forge a `mhj-banner` message.
  if (!sessionToken || raw.token !== sessionToken) return null

  // Omitting `action` means "only log the click". Naming one we don't recognise is an authoring
  // mistake, so the message is dropped rather than logged as an interaction that never ran (#261690).
  const action = asString(raw.action)
  if (raw.action !== undefined && !isContentAction(action)) return null

  return {
    action: isContentAction(action) ? action : null,
    params: sanitizeParams(isRecord(raw.params) ? raw.params : {}),
    ctaText: asString(raw.ctaText),
  }
}
